import { PortalLoginView, type PortalLoginActionViewModel } from '@workspace/silk-ui';

import { usePortalLoginController } from '@/features/portal-login/usePortalLoginController';

interface PortalLoginPageProps {
  signedOut?: boolean;
}

export default function PortalLoginPage({ signedOut = false }: PortalLoginPageProps) {
  const login = usePortalLoginController();
  const pending = login.state === 'loading' || login.state === 'redirecting';
  const unavailable = login.state === 'unavailable';
  const primary: PortalLoginActionViewModel = login.primaryAction
    ? {
        id: login.primaryAction.source,
        label: login.primaryAction.label,
        description: login.mode === 'tenant'
          ? `Use the identity provider configured by ${login.tenantName ?? 'this tenant'}.`
          : 'Sign in with your platform account.',
        disabled: pending,
        pending: login.state === 'redirecting',
        onInvoke: login.onPrimary,
      }
    : {
        id: unavailable ? 'retry' : 'platform_public',
        label: unavailable ? 'Try again' : 'Loading sign-in options…',
        disabled: !unavailable,
        pending: login.state === 'loading',
        onInvoke: login.onRetry,
      };

  return (
    <PortalLoginView
      mode={unavailable ? 'unavailable' : login.mode}
      productName="ConnectPlane"
      tenantName={login.tenantName}
      eyebrow={login.mode === 'tenant' ? 'Tenant portal' : 'Account portal'}
      title={login.mode === 'tenant' && login.tenantName ? `Sign in to ${login.tenantName}` : 'Sign in to your account'}
      description={login.mode === 'tenant'
        ? 'Choose your tenant identity provider or use your platform account.'
        : 'Continue with your platform account. Tenant sign-in is available from your tenant-specific portal address.'}
      status={login.message
        ? { tone: 'danger', title: 'Sign-in is unavailable', message: login.message }
        : signedOut
          ? { tone: 'success', title: 'Signed out', message: 'Your portal session ended securely.' }
          : null}
      primaryAction={primary}
      secondaryAction={login.fallbackAction
        ? {
            id: 'platform_public',
            label: login.fallbackAction.label,
            description: 'Use your platform account instead.',
            disabled: pending,
            pending: login.state === 'redirecting',
            onInvoke: login.onFallback,
          }
        : null}
      registrationAction={login.registrationAction
        ? {
            id: 'register',
            label: login.registrationAction.label,
            disabled: pending,
            pending: login.state === 'redirecting',
            onInvoke: login.onRegister,
          }
        : null}
    />
  );
}
